import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { EnrichedCompany } from '@/hooks/useCompanyProfile';

// A minimal shape that both EnrichedCompany and any comparable object must satisfy
export interface ComparableCompany {
  id: string;
  name: string;
  description_short: string;
  modality: string[];
  indications: string[];
  stage: string | null;
  location: string | null;
  funding_stage: string | null;
  total_funding: string | null;
  key_partners: string[];
  core_platform: string;
  pipeline: { program_name: string; indication: string; stage: string }[];
  platform_focus?: string[];
}

interface CompareContextType {
  compareList: ComparableCompany[];
  addToCompare: (company: ComparableCompany) => boolean;
  removeFromCompare: (companyId: string) => void;
  clearCompare: () => void;
  isInCompare: (companyId: string) => boolean;
  canAdd: boolean;
}

const CompareContext = createContext<CompareContextType | undefined>(undefined);

const MAX_COMPARE = 5;

export function CompareProvider({ children }: { children: ReactNode }) {
  const [compareList, setCompareList] = useState<ComparableCompany[]>([]);

  const addToCompare = useCallback((company: ComparableCompany) => {
    let added = false;
    setCompareList((prev) => {
      if (prev.length >= MAX_COMPARE) return prev;
      if (prev.some((c) => c.id === company.id)) return prev;
      added = true;
      return [...prev, company];
    });
    return added;
  }, []);

  const removeFromCompare = useCallback((companyId: string) => {
    setCompareList((prev) => prev.filter((c) => c.id !== companyId));
  }, []);

  const clearCompare = useCallback(() => setCompareList([]), []);

  const isInCompare = useCallback(
    (companyId: string) => compareList.some((c) => c.id === companyId),
    [compareList]
  );

  return (
    <CompareContext.Provider
      value={{
        compareList,
        addToCompare,
        removeFromCompare,
        clearCompare,
        isInCompare,
        canAdd: compareList.length < MAX_COMPARE,
      }}
    >
      {children}
    </CompareContext.Provider>
  );
}

export function useCompare() {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used within CompareProvider');
  return ctx;
}
